/**
 * Tutorial Primer — 6-card carousel shown on first Bot Match.
 *
 * Phase 3.8: added BACK/PREVIOUS button, tappable step dots, and
 * mobile swipe left/right between cards. Skip always visible.
 *
 * Triggered when:
 *   - User taps Play vs Bot
 *   - localStorage `settings.tutorialPrimerSeen` is false
 */
import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { TUTORIAL_PRIMER } from '@/lib/tutorialConfig';

const SWIPE_THRESHOLD = 50; // px

export default function TutorialPrimer({ onSkip, onStartMatch }) {
  const [i, setI] = useState(0);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  const touchStartXRef = useRef(null);
  const last = i === TUTORIAL_PRIMER.length - 1;
  const first = i === 0;
  const card = TUTORIAL_PRIMER[i];

  const goTo = (next) => {
    if (next < 0 || next >= TUTORIAL_PRIMER.length) return;
    setDirection(next > i ? 1 : -1);
    setI(next);
  };
  const goNext = () => goTo(i + 1);
  const goBack = () => goTo(i - 1);

  // ---- Touch swipe (mobile) ----
  const onTouchStart = (e) => {
    if (!e.touches || e.touches.length === 0) return;
    touchStartXRef.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    const startX = touchStartXRef.current;
    touchStartXRef.current = null;
    if (startX == null) return;
    const endX = (e.changedTouches && e.changedTouches[0]?.clientX) ?? startX;
    const dx = endX - startX;
    if (dx <= -SWIPE_THRESHOLD) {
      if (!last) goNext();
    } else if (dx >= SWIPE_THRESHOLD) {
      if (!first) goBack();
    }
  };

  return (
    <div
      data-testid="tutorial-primer"
      className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex flex-col items-center justify-center px-6 py-10 overflow-y-auto"
      style={{ overscrollBehavior: 'contain' }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="absolute top-4 right-4">
        <button
          data-testid="tutorial-skip"
          className="font-heading text-xs tracking-[0.28em] text-white/60 hover:text-white px-3 py-1 rounded-full border border-white/15 active:scale-95 transition-transform"
          onClick={onSkip}
        >
          SKIP
        </button>
      </div>

      <div className="font-heading text-[10px] tracking-[0.3em] text-[hsl(var(--k-gold-bright))] uppercase mb-2">
        How to Play · 60 sec
      </div>

      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={card.id}
          data-testid={card.testid}
          custom={direction}
          initial={{ opacity: 0, x: direction * 30 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -30 }}
          transition={{ duration: 0.24 }}
          className="w-full max-w-[340px] text-center select-none"
        >
          <div className="font-heading text-4xl tracking-[0.06em] text-white mb-6 uppercase">
            {card.title}
          </div>
          <div className="space-y-2">
            {card.lines.map((line, idx) => (
              <div
                key={idx}
                className="font-body text-base text-white/85 leading-snug"
              >
                {line}
              </div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Tappable step dots */}
      <div
        data-testid="tutorial-dots"
        className="flex gap-2 mt-10 mb-6"
        role="tablist"
        aria-label="Tutorial steps"
      >
        {TUTORIAL_PRIMER.map((c, idx) => (
          <button
            key={c.id}
            type="button"
            data-testid={`tutorial-dot-${idx}`}
            aria-label={`Go to step ${idx + 1}`}
            aria-current={idx === i ? 'step' : undefined}
            onClick={() => goTo(idx)}
            className="w-3 h-3 rounded-full transition-colors flex items-center justify-center active:scale-90"
            style={{
              background: idx === i ? 'hsl(var(--k-gold-bright))' : 'rgba(255,255,255,0.22)',
              boxShadow: idx === i ? '0 0 10px hsl(var(--k-gold) / 0.7)' : 'none',
              padding: 0,
              border: 'none',
            }}
          />
        ))}
      </div>

      <div className="w-full max-w-[340px] flex items-center gap-2">
        <button
          type="button"
          data-testid="tutorial-back"
          aria-label="Previous step"
          aria-disabled={first}
          disabled={first}
          onClick={goBack}
          className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center border border-white/15 text-white/80 active:scale-95 transition-all"
          style={{
            opacity: first ? 0.25 : 1,
            cursor: first ? 'not-allowed' : 'pointer',
            background: 'rgba(255,255,255,0.04)',
          }}
        >
          <ChevronLeft size={20} strokeWidth={2.25} />
        </button>

        {!last ? (
          <button
            data-testid="tutorial-next"
            className="cta-pill cta-pill--primary flex-1 py-3.5 text-sm flex items-center justify-center gap-1"
            onClick={goNext}
          >
            <span>Next</span>
            <ChevronRight size={16} strokeWidth={2.25} />
          </button>
        ) : (
          <button
            data-testid="tutorial-start-match"
            className="cta-pill cta-pill--primary flex-1 py-3.5 text-sm"
            onClick={onStartMatch}
          >
            Start Match
          </button>
        )}
      </div>
    </div>
  );
}
